import { useEffect, useRef } from 'react';

// One shared tooltip for every element with a data-tip attribute (and an optional data-key shortcut, such as "G T").
// It shows after a short pause on hover, or straight away when a control is reached with the keyboard.

const DELAY = 380;
/** Longer tips wrap onto a few lines instead of running across the screen. */
const WRAP_AFTER = 90;

function keysHtml(keys: string | null): string {
  if (!keys) return '';
  return keys
    .split(' ')
    .map((k) => `<kbd class="kbd">${escapeHtml(k)}</kbd>`)
    .join('');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

export function TooltipHost() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tip = ref.current!;
    let target: HTMLElement | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const hide = () => {
      clearTimeout(timer);
      target = null;
      tip.classList.remove('show');
    };
    const show = (el: HTMLElement) => {
      const text = el.getAttribute('data-tip');
      if (!text || !el.isConnected) return;
      target = el;
      tip.innerHTML = `<span>${escapeHtml(text)}</span>${keysHtml(el.getAttribute('data-key'))}`;
      tip.classList.toggle('wrap', text.length > WRAP_AFTER);
      tip.style.left = '0px';
      tip.style.top = '0px';
      const r = el.getBoundingClientRect();
      const w = tip.offsetWidth;
      const h = tip.offsetHeight;
      let x = r.left + r.width / 2 - w / 2;
      let y = r.bottom + 8;
      if (y + h > window.innerHeight - 6) y = r.top - h - 8;
      x = Math.max(6, Math.min(window.innerWidth - w - 6, x));
      tip.style.left = `${x}px`;
      tip.style.top = `${y}px`;
      tip.classList.add('show');
    };
    const schedule = (el: HTMLElement | null, delay: number) => {
      if (el === target) return;
      hide();
      if (!el) return;
      target = el;
      timer = setTimeout(() => {
        if (target === el) show(el);
      }, delay);
    };

    const over = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.<HTMLElement>('[data-tip]') ?? null;
      schedule(el, DELAY);
    };
    const focusIn = (e: FocusEvent) => {
      const el = e.target instanceof HTMLElement ? e.target : null;
      if (!el || !el.matches(':focus-visible') || !el.hasAttribute('data-tip')) return;
      schedule(el, DELAY);
    };
    const focusOut = () => hide();
    const keyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') hide();
    };

    document.addEventListener('mouseover', over);
    document.addEventListener('focusin', focusIn);
    document.addEventListener('focusout', focusOut);
    document.addEventListener('mousedown', hide, true);
    document.addEventListener('scroll', hide, true);
    document.addEventListener('keydown', keyDown, true);
    window.addEventListener('resize', hide);
    window.addEventListener('blur', hide);
    return () => {
      hide();
      document.removeEventListener('mouseover', over);
      document.removeEventListener('focusin', focusIn);
      document.removeEventListener('focusout', focusOut);
      document.removeEventListener('mousedown', hide, true);
      document.removeEventListener('scroll', hide, true);
      document.removeEventListener('keydown', keyDown, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('blur', hide);
    };
  }, []);
  return <div ref={ref} className="tip" aria-hidden="true" />;
}
